import React, { useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import LoadingPage from "../../common/LoadingPage";
import { showErrorToast } from "../../toastUtils";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import {
  Prompt,
  useCreatePromptDocumentMutation,
  useCreatePromptDocumentRefinementMutation,
  useCreateTranscribedTextPromptMutation,
} from "../../store/userApi";
import { GlobalPrompt } from "../../store/promptsSlice";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { otterTheme, darkTheme } from "../../theme";
import { copyToClipboard } from "../../common/commonFuncs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { logAnalyticsEvent } from "../../config/firebase";
import { logPosthogEvent } from "../../common/posthogUtils";
import { API_URL } from "../../config";
import { useAuth } from "@clerk/clerk-react";
import { useAppSettings } from "../../contexts/AppSettingsContext";

// Create a union type that can be either a user prompt or global prompt
type PromptType = Prompt | GlobalPrompt;

// Define the SecondaryPrompt interface similar to what's in the Prompt model
interface SecondaryPrompt {
  id: string;
  title: string;
  promptContent: string;
}

interface PrepareEmailTabProps {
  prompt: PromptType;
  handleDownload: (text: string | null, filename: string) => void;
  inputText: string;
  overrideText?: string | null;
  overrideTitle?: string;
}

const PromptTab: React.FC<PrepareEmailTabProps> = ({
  prompt,
  handleDownload,
  inputText,
  overrideText,
  overrideTitle,
}) => {
  const { settings } = useAppSettings();
  const lightTheme = otterTheme; // Updated theme to align with SummarizationTab
  const [text, setText] = useState<string>(inputText);
  const [refinedText, setRefinedText] = useState<string | null>(null);
  const [selectedPrimaryButton, setSelectedPrimaryButton] = useState<
    null | string
  >(null);
  const [selectedSecondaryButton, setSelectedSecondaryButton] = useState<
    null | string
  >(null);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const user = useSelector((state: RootState) => state.user);
  const { getToken } = useAuth();

  const [getDocumentPrompt, { isLoading: creatingDocumentPrompt }] =
    useCreatePromptDocumentMutation();
  const [
    getDocumentRefinementPrompt,
    { isLoading: creatingDocumentRefinementPrompt },
  ] = useCreatePromptDocumentRefinementMutation();

  const [
    getTranscribedTextDocument,
    { isLoading: gettingTranscribedTextDocument },
  ] = useCreateTranscribedTextPromptMutation();

  const isLoading =
    creatingDocumentPrompt ||
    creatingDocumentRefinementPrompt ||
    gettingTranscribedTextDocument ||
    isLoadingPrompt;

  // Check if this is a global prompt (doesn't have primaryPrompts property)
  const isGlobalPrompt = !('primaryPrompts' in prompt);

  // Add an abort controller for canceling pending requests
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const handleInitialTranscribedTextPrompt = async () => {
    // Create a new AbortController
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if (isGlobalPrompt) {
        // For global prompts, use the promptContent directly
        const result = await processGlobalPrompt(prompt.promptContent, inputText, signal);
        if (!signal.aborted) {
          setText(result);
        }
      } else {
        // For user-specific prompts, use the existing API
        try {
          logAnalyticsEvent("handleInitialTranscribedTextPrompt", {
            funcName: "handleInitialTranscribedTextPrompt",
          });
          logPosthogEvent(
            "handleInitialTranscribedTextPrompt",
            { funcName: "handleInitialTranscribedTextPrompt" },
            user.user?.email,
          );
          const response = await getTranscribedTextDocument({
            id: prompt.id,
            text: inputText,
          }).unwrap();
          
          // Check if the request was aborted before updating state
          if (!signal.aborted) {
            setText(response.gptTextResponse);
          }
        } catch (error) {
          if (!signal.aborted) {
            showErrorToast(`Failed to get document ${getErrorMessage(error)}`);
          }
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        console.error("Error in handleInitialTranscribedTextPrompt:", error);
      }
    }
  };

  // Update processGlobalPrompt to accept and use the AbortSignal
  const processGlobalPrompt = async (promptContent: string, inputContent: string, signal?: AbortSignal) => {
    setIsLoadingPrompt(true);
    try {
      // Replace placeholders in the prompt content
      const processedContent = promptContent.replace(/{{transcription}}/g, inputContent);
      
      // Call the backend API to process the global prompt
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const userId = localStorage.getItem('clerk-user-id') || '';
      console.log('Debug: Using clerk-user-id for prompt processing:', {
        hasUserId: !!userId,
        userId: userId ? userId.substring(0, 8) + '...' : 'missing', // Only log part of the ID for security
        promptId: prompt.id // Log the prompt ID to confirm it's being sent
      });
      
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({ 
          promptContent: processedContent,
          promptId: prompt.id // Include the actual prompt ID
        })
      };
      
      // Add the signal to the fetch options if it exists
      if (signal) {
        fetchOptions.signal = signal;
      }
      
      const response = await fetch(`${API_URL}/prompts/process`, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to process prompt: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.result;
    } catch (error) {
      // Only show errors if the request wasn't aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request was aborted');
        return "";
      }
      
      console.error('Error processing global prompt:', error);
      showErrorToast(`Failed to process prompt: ${getErrorMessage(error)}`);
      return "Error processing prompt. Please try again.";
    } finally {
      // Only update loading state if we're still mounted
      if (!signal?.aborted) {
        setIsLoadingPrompt(false);
      }
    }
  };

  const getGptResponse = async (primaryPromptId: string) => {
    try {
      logAnalyticsEvent("getDocumentPrompt", {
        primaryPromptId,
        funcName: "getGptResponse",
      });
      logPosthogEvent(
        "getDocumentPrompt",
        { primaryPromptId, funcName: "getGptResponse" },
        user.user?.email,
      );
      const response = await getDocumentPrompt({
        id: prompt.id,
        primaryPromptId,
        text: inputText,
      }).unwrap();
      setRefinedText(null);
      setText(response.gptTextResponse);
    } catch (error) {
      showErrorToast(getErrorMessage(error));
    }
  };

  const handlePrimaryButtonClick = (primaryPromptId: string) => {
    if (isLoading) return;
    setSelectedPrimaryButton(primaryPromptId);
    setSelectedSecondaryButton(null);
    getGptResponse(primaryPromptId);
  };

  const handleSecondaryButtonClick = async (secondaryPromptId: string) => {
    if (isLoading) return;
    setSelectedSecondaryButton(secondaryPromptId);
    try {
      logAnalyticsEvent("getDocumentRefinementPrompt", {
        secondaryPromptId,
        funcName: "handleSecondaryButtonClick",
      });
      logPosthogEvent(
        "getDocumentRefinementPrompt",
        { secondaryPromptId, funcName: "handleSecondaryButtonClick" },
        user.user?.email,
      );
      const response = await getDocumentRefinementPrompt({
        id: prompt.id,
        text,
        secondaryPromptId,
      }).unwrap();
      setRefinedText(response.gptTextResponse);
    } catch (error) {
      showErrorToast(getErrorMessage(error));
    }
  };

  // Text to display - use override if provided, otherwise use normal flow
  const displayText = overrideText || refinedText || text || '';
  // Title to display - use override if provided, otherwise use prompt title
  const displayTitle = overrideTitle || prompt.title;

  useEffect(() => {
    // Only call handleInitialTranscribedTextPrompt if no override text is provided
    if (!overrideText) {
      handleInitialTranscribedTextPrompt();
    }
    
    // Cleanup function to abort any in-progress requests when component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [prompt.id]); // Add prompt.id as a dependency to ensure initialization happens on prompt change

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: darkTheme.palette.background.default,
        padding: "16px",
      }}
    >
      {/* Only show Primary/Secondary buttons for user-specific prompts */}
      {!isGlobalPrompt && (
        <>
      {/* Primary Button Group */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: "600px",
          width: "100%",
          padding: "16px",
        }}
      >
        <ButtonGroup
          disabled={isLoading}
          variant="outlined"
          aria-label="primary button group"
          sx={{
            mb: 2,
            flexWrap: "wrap",
            "& .MuiButton-root": {
                  borderColor: settings.primaryColor,
              color: "#ffffff",
              borderRadius: "24px",
              padding: "6px 16px",
              textTransform: "none",
              transition: "background-color 0.3s, border-color 0.3s",
              margin: "4px",
              "&:hover": {
                backgroundColor: lightTheme.palette.primary.light,
                color: lightTheme.palette.common.white,
                borderColor: "transparent",
              },
              "&.Mui-disabled": {
                color: "#ffffff",
                opacity: 0.6,
                    borderColor: settings.primaryColor,
              },
            },
          }}
        >
              {!isGlobalPrompt && (prompt as Prompt).primaryPrompts && (prompt as Prompt).primaryPrompts.map((primaryPrompt) => (
            <Button
              key={primaryPrompt.id}
              onClick={() => handlePrimaryButtonClick(primaryPrompt.id)}
              sx={{
                borderColor: "transparent",
                color:
                  selectedPrimaryButton === primaryPrompt.id
                    ? lightTheme.palette.common.white
                        : settings.primaryColor,
                backgroundColor:
                  selectedPrimaryButton === primaryPrompt.id
                        ? settings.primaryColor
                    : "transparent",
                margin: "4px",
                "&:hover": {
                  backgroundColor: lightTheme.palette.primary.light,
                  color: lightTheme.palette.common.white,
                  borderColor: "transparent",
                },
                "&.Mui-disabled": {
                  color: "#ffffff",
                  opacity: 0.6,
                      borderColor: settings.primaryColor,
                },
              }}
            >
              {primaryPrompt.title}
            </Button>
          ))}
        </ButtonGroup>

            {/* Secondary buttons - only show if a primary button is selected */}
            {selectedPrimaryButton && !isGlobalPrompt && (prompt as Prompt).primaryPrompts && (
        <ButtonGroup
          disabled={isLoading}
          variant="outlined"
          aria-label="secondary button group"
          sx={{
            flexWrap: "wrap",
            mb: 2,
            flexDirection: "row",
            "& .MuiButton-root": {
                    borderColor: settings.primaryColor,
              color: "#ffffff",
              borderRadius: "24px",
              padding: "6px 16px",
              textTransform: "none",
              transition: "background-color 0.3s, border-color 0.3s",
                    margin: "4px",
                    "&:hover": {
                      backgroundColor: lightTheme.palette.primary.light,
                      color: lightTheme.palette.common.white,
                      borderColor: "transparent",
                    },
                    "&.Mui-disabled": {
                      color: "#ffffff",
                      opacity: 0.6,
                      borderColor: settings.primaryColor,
                    },
                  },
                }}
              >
                {(prompt as Prompt).primaryPrompts
                  .find((p) => p.id === selectedPrimaryButton)
                  ?.secondaryPrompts?.map((secondaryPrompt: SecondaryPrompt) => (
                    <Button
                      key={secondaryPrompt.id}
                      onClick={() =>
                        handleSecondaryButtonClick(secondaryPrompt.id)
                      }
                      sx={{
                        borderColor: "transparent",
                        color:
                          selectedSecondaryButton === secondaryPrompt.id
                            ? lightTheme.palette.common.white
                            : settings.primaryColor,
                        backgroundColor:
                          selectedSecondaryButton === secondaryPrompt.id
                            ? settings.primaryColor
                            : "transparent",
                        "&:hover": {
                          backgroundColor: lightTheme.palette.primary.light,
                          color: lightTheme.palette.common.white,
                          borderColor: "transparent",
                    },
                  }}
                >
                  {secondaryPrompt.title}
                </Button>
              ))}
        </ButtonGroup>
            )}
      </Box>
        </>
      )}

      {/* Text display area */}
      <Box
        sx={{
          width: "80%",
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "20px",
          color: "#333",
          maxHeight: "500px",
          overflow: "auto",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant="h6" color="#333">
            {displayTitle}
          </Typography>
          <Box>
            {!overrideText && !isGlobalPrompt && (prompt as Prompt).primaryPrompts && (prompt as Prompt).primaryPrompts.length > 0 && (
              <ButtonGroup size="small" sx={{ mb: 2, mt: 1 }}>
                {(prompt as Prompt).primaryPrompts.map((primaryPrompt) => (
                  <Button
                    key={primaryPrompt.id}
                    variant={
                      selectedPrimaryButton === primaryPrompt.id
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => handlePrimaryButtonClick(primaryPrompt.id)}
                    sx={{
                      textTransform: "none",
                      ...(selectedPrimaryButton === primaryPrompt.id
                        ? {
                            backgroundColor: settings.primaryColor || lightTheme.palette.primary.main,
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: settings.secondaryColor || lightTheme.palette.primary.dark,
                            },
                          }
                        : {
                            color: settings.primaryColor || lightTheme.palette.primary.main,
                            borderColor: settings.primaryColor || lightTheme.palette.primary.main,
                            "&:hover": {
                              borderColor: settings.secondaryColor || lightTheme.palette.primary.dark,
                            },
                          }),
                    }}
                  >
                    {primaryPrompt.title}
                  </Button>
                ))}
              </ButtonGroup>
            )}
            
            {!overrideText && !isGlobalPrompt && selectedPrimaryButton && (prompt as Prompt).primaryPrompts && (
              <ButtonGroup size="small" sx={{ mb: 2 }}>
                {(prompt as Prompt).primaryPrompts
                  .find(p => p.id === selectedPrimaryButton)
                  ?.secondaryPrompts?.map((secondaryPrompt: SecondaryPrompt) => (
                    <Button
                      key={secondaryPrompt.id}
                      variant={
                        selectedSecondaryButton === secondaryPrompt.id
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() =>
                        handleSecondaryButtonClick(secondaryPrompt.id)
                      }
                      sx={{
                        textTransform: "none",
                        ...(selectedSecondaryButton === secondaryPrompt.id
                          ? {
                              backgroundColor: settings.primaryColor || lightTheme.palette.primary.main,
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: settings.secondaryColor || lightTheme.palette.primary.dark,
                              },
                            }
                          : {
                              color: settings.primaryColor || lightTheme.palette.primary.main,
                              borderColor: settings.primaryColor || lightTheme.palette.primary.main,
                              "&:hover": {
                                borderColor: settings.secondaryColor || lightTheme.palette.primary.dark,
                              },
                            }),
                      }}
                    >
                      {secondaryPrompt.title}
                    </Button>
                  ))}
              </ButtonGroup>
            )}
            
            <Button
              sx={{
                textTransform: "none",
                color: settings.primaryColor || "#333",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.05)",
                },
                padding: "4px 8px",
                minWidth: "unset",
              }}
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => copyToClipboard(displayText)}
            >
              Copy
            </Button>
            <Button
              color="inherit"
              sx={{
                ml: 1,
                textTransform: "none",
                color: settings.primaryColor || "#333",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.05)",
                },
              }}
              onClick={() =>
                handleDownload(
                  displayText,
                  `transcribe_${prompt.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                )
              }
            >
              Download
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <LoadingPage />
        ) : (
          <Box
            sx={{
              backgroundColor: refinedText ? "#f8f8f8" : "transparent",
              padding: refinedText ? "16px" : "0",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <ReactMarkdown className="markdown-content">
              {displayText}
            </ReactMarkdown>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default PromptTab;
